import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { Button } from "../components/ui/button";
import { gradeColorClass } from "../components/ReportSheet";
import { ArrowLeft, Printer, Download } from "lucide-react";

export default function ClassRecap() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/classes/${classId}/scores`).then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Gagal memuat"));
  }, [classId]);
  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    const ac = data.academic_components, na = data.non_academic_components;
    const head = ["No", "Nama Siswa", "Kelas", "Buku/Level", ...ac.map((c) => c.label), "Nilai Angka", "Nilai Huruf", ...na.map((c) => c.label)];
    const rows = data.students.map((s) => {
      const sc = data.scores[s.id] || {};
      return [s.number || "", s.name, s.class_in_school || "", s.book_level || "",
        ...ac.map((c) => sc.academic?.[c.key] ?? ""), sc.average ?? "", sc.letter ?? "", ...na.map((c) => sc.non_academic?.[c.key] ?? "")];
    });
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `Rekap-Nilai-${(data.class.school_name || "").replace(/\s+/g, "-")}-${(data.class.class_in_school || data.class.name).replace(/\s+/g, "-")}-Semester-${data.class.semester}-${data.class.academic_year}.csv`;
    a.click();
  };

  if (!data) return <div className="p-8 text-muted-foreground">Memuat...</div>;
  const c = data.class, ac = data.academic_components, na = data.non_academic_components;
  const today = new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="space-y-4" data-testid="class-recap-page">
      <Toaster position="top-center" richColors />
      <style>{`@media print { .no-print { display:none !important; } @page { size: A4 landscape; margin: 12mm; } body { -webkit-print-color-adjust: exact; } }`}</style>

      <div className="flex items-center justify-between flex-wrap gap-2 no-print">
        <Button variant="outline" size="sm" onClick={() => navigate(`/classes/${classId}/scores`)} className="rounded-full gap-1" data-testid="recap-back"><ArrowLeft className="w-4 h-4" /> Input Nilai</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCSV} className="rounded-full gap-1" data-testid="recap-export"><Download className="w-4 h-4" /> Export CSV</Button>
          <Button onClick={() => window.print()} className="rounded-full bg-gtsnavy text-white gap-1" data-testid="recap-print"><Printer className="w-4 h-4" /> Print / PDF</Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 print:shadow-none print:p-0" data-testid="recap-sheet">
        <div className="text-center mb-4">
          <h1 className="font-nunito font-extrabold text-lg md:text-xl text-gtsnavy uppercase">Rekapitulasi Nilai Ekskul Bahasa Inggris {c.school_name}</h1>
          <p className="text-sm text-muted-foreground uppercase">Tahun Pelajaran {c.academic_year}</p>
        </div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4 max-w-2xl">
          <p><b>Kelas/Program:</b> {c.name}{c.class_in_school ? ` (${c.class_in_school})` : ""}</p>
          <p><b>Semester:</b> {c.semester}</p>
          <p><b>Guru:</b> {c.teacher_name}</p>
          <p><b>Buku/Level:</b> {c.book_level || "-"}</p>
          <p><b>Sekolah:</b> {c.school_name}</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse" data-testid="recap-table">
            <thead>
              <tr className="bg-gtsnavy/10 text-gtsnavy">
                <th className="border border-slate-300 px-2 py-1.5">No</th>
                <th className="border border-slate-300 px-2 py-1.5 text-left">Nama Siswa</th>
                <th className="border border-slate-300 px-2 py-1.5">Kelas</th>
                <th className="border border-slate-300 px-2 py-1.5">Buku/Level</th>
                {ac.map((x) => <th key={x.key} className="border border-slate-300 px-2 py-1.5">{x.label}</th>)}
                <th className="border border-slate-300 px-2 py-1.5 bg-gtsorange/20">Nilai Angka</th>
                <th className="border border-slate-300 px-2 py-1.5 bg-gtsorange/20">Nilai Huruf</th>
                {na.map((x) => <th key={x.key} className="border border-slate-300 px-2 py-1.5">{x.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.students.map((s) => {
                const sc = data.scores[s.id] || {};
                return (
                  <tr key={s.id} data-testid={`recap-row-${s.id}`}>
                    <td className="border border-slate-300 px-2 py-1 text-center">{s.number}</td>
                    <td className="border border-slate-300 px-2 py-1">{s.name}</td>
                    <td className="border border-slate-300 px-2 py-1 text-center">{s.class_in_school || "-"}</td>
                    <td className="border border-slate-300 px-2 py-1 text-center">{s.book_level || "-"}</td>
                    {ac.map((x) => <td key={x.key} className="border border-slate-300 px-2 py-1 text-center">{sc.academic?.[x.key] ?? "-"}</td>)}
                    <td className="border border-slate-300 px-2 py-1 text-center font-bold">{sc.average ?? "-"}</td>
                    <td className={`border border-slate-300 px-2 py-1 text-center font-bold ${gradeColorClass(sc.letter)}`}>{sc.letter ?? "-"}</td>
                    {na.map((x) => <td key={x.key} className="border border-slate-300 px-2 py-1 text-center">{sc.non_academic?.[x.key] ?? "-"}</td>)}
                  </tr>
                );
              })}
              {data.students.length === 0 && <tr><td colSpan={4 + ac.length + na.length + 2} className="border border-slate-300 px-2 py-6 text-center text-muted-foreground">Belum ada siswa.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between mt-8 text-sm">
          <div />
          <div className="text-center">
            <p>Bekasi, {today}</p>
            <p className="mb-12">IEC Mustikajaya</p>
            <p className="font-semibold border-t border-slate-400 pt-1 inline-block px-6">{c.teacher_name}</p>
            <p className="text-xs text-muted-foreground">Guru Kelas</p>
          </div>
        </div>
      </div>
    </div>
  );
}
